import * as React from "react";
import styled, { ThemeProvider, css } from "styled-components";

import { Props } from "./MembershipCard";
import { User, Profile } from "../entities";
import { createEntitiesConsumer } from "../../lib/entities";
import Hover from "../../common/components/Hover";
import Icon from "../../common/components/Icon";
import ImageContainer from "../../common/components/ImageContainer";
import { media, truncateLines } from "../../common/utils/style";
import { FilledStarIcon, HandlerIconWrapper } from "./MembershipCard";

const UserEntitiesConsumer = createEntitiesConsumer<{ user: User }>({
  user: "users",
});

const ProfileEntitiesConsumer = createEntitiesConsumer<{ profile: Profile }>({
  profile: "profiles",
});

class FeaturedMembershipCard extends React.PureComponent<Props> {
  public onClickMembership = (event: React.MouseEvent<HTMLElement>) => {
    if (this.props.editable) {
      event.preventDefault();
    }
  };

  public onTouchMembership = (event: React.TouchEvent<HTMLElement>) => {
    const { onMouseEnter, membership, isEntered } = this.props;
    if (onMouseEnter && !isEntered) {
      onMouseEnter(membership);
      event.preventDefault();
    }
  };

  public onMouseEnterMembership = () => {
    const { onMouseEnter, membership, isEntered } = this.props;
    if (onMouseEnter && !isEntered) {
      onMouseEnter(membership);
    }
  };

  public onClickStar = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    const { isFeatured, membership, onFeatured, onUnfeatured } = this.props;
    if (isFeatured) {
      if (onUnfeatured) {
        onUnfeatured(membership);
      }
    } else {
      if (onFeatured) {
        onFeatured(membership);
      }
    }
  };

  public render() {
    const { membership } = this.props;
    return <UserEntitiesConsumer query={{ user: membership.user }}>{this.renderWithUser}</UserEntitiesConsumer>;
  }

  private renderWithUser: typeof UserEntitiesConsumer.children = ({ user }) => {
    if (!user) {
      return null;
    }
    const { editable, isEntered } = this.props;
    return (
      <ThemeProvider theme={{ ...this.props }}>
        <Hover>
          {({ isHovered, ref }) => (
            <Base
              innerRef={ref}
              href={user.user_path}
              onMouseEnter={this.onMouseEnterMembership}
              onMouseUp={this.onClickMembership}
              onTouchEnd={this.onTouchMembership}
              onClick={this.onClickMembership}
            >
              <AvatarWrapper>
                <Avatar>
                  <AvatarImageCover
                    namespace="companies"
                    id={user.avatar}
                    size="120x120#"
                    alt={user.name}
                    draggable={false}
                    lazy={false}
                    background
                  />
                </Avatar>
              </AvatarWrapper>
              {isEntered ? (
                <SelectedLine />
              ) : (
                <MemberDescriptor>
                  <Name>{user.name}</Name>
                  <ProfileEntitiesConsumer query={{ profile: user.profile }}>
                    {({ profile }) => profile && <Position>{profile.position}</Position>}
                  </ProfileEntitiesConsumer>
                </MemberDescriptor>
              )}
              {editable && (
                <>
                  <FilledStarIcon name="wt-icon-star" onClick={this.onClickStar} />
                  {isHovered && (
                    <HandlerIconWrapper>
                      <Icon name="wt-icon-bers" />
                    </HandlerIconWrapper>
                  )}
                </>
              )}
            </Base>
          )}
        </Hover>
      </ThemeProvider>
    );
  };
}

export default FeaturedMembershipCard;

const Base = styled.a`
  position: relative;
  display: block;
  width: 100%;
  max-width: 180px;
  margin: 0 auto;
  padding-bottom: 19px;
  border-radius: 10px;
  text-align: center;
  border: 1px solid white;
  cursor: ${props => (props.theme.editable ? "move" : "pointer")};
  &:hover {
    text-decoration: none;
  }
  @media ${media.medium} {
    padding: 20px 0 29px 0;
  }
  ${props =>
    props.theme.editable &&
    css`
      transition: background-color 150ms ease-out;
      &:hover {
        background-color: rgba(245, 245, 245, 1);
      }
    `};
`;

const AvatarWrapper = styled.div`
  position: relative;
  margin: 0 auto 8px auto;
  width: 70%;
  max-width: 110px;
  @media ${media.large} {
    margin: 0 auto 19px auto;
  }
  &:before {
    content: "";
    display: block;
    padding-top: 100%;
  }
`;

// [NOTE(kobayang)] A tricky solution that height equals width with pure css
// http://www.mademyday.de/css-height-equals-width-with-pure-css.html
const Avatar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
`;

const AvatarImageCover = styled(ImageContainer)`
  border-radius: 50%;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  transition: transform 150ms ease-out;
  ${props =>
    props.theme.isEntered &&
    css`
      transform: scale(1.06);
      @media ${media.medium} {
        transform: scale(1.27);
      }
    `};
`;

const MemberDescriptor = styled.div`
  padding: 0 15px;
  margin-top: 6px;
  text-align: center;
`;

const Name = styled.div`
  display: none;
  font-weight: 600;
  line-height: 1.17;
  color: rgba(0, 0, 0, 0.8);
  font-size: 18px;
  @media ${media.medium} {
    display: block;
    font-size: 14px;
  }
`;

const Position = styled.div`
  font-weight: 300;
  margin-top: 8px;
  color: rgba(0, 0, 0, 0.4);
  ${truncateLines(1, 11, 1)};
  @media ${media.medium} {
    font-weight: 400;
    ${truncateLines(1, 13, 1)};
  }
`;

const SelectedLine = styled.div`
  border-radius: 1.5px;
  background-color: ${props => props.theme.lineColor};
  margin: 19px auto 0 auto;
  width: 12%;
  height: 2px;
  @media ${media.medium} {
    margin: 50px auto 0 auto;
    width: 29px;
    height: 3px;
  }
`;



// WEBPACK FOOTER //
// ./frontend/src/company_memberships/components/FeaturedMembershipCard.tsx